import { BriefResponseDto } from '../../../../common/general-types/briefs.dto';
import { CreateBriefPaymentMethodDto } from '../../../../common/general-types/shout-plus.dto';
import {
  WalletTransactionResponseDto,
  WalletTransactionTypeEnum,
} from '../../../../common/general-types/wallet-transaction.dto';
import {
  crossPostNetTotal,
  hireCostBreakdown,
  VAT_RATE,
} from '../../../manage-brief-tabs/tabs/assignment/hire-cost.util';
import { SubmittedPostStatusEnum } from '../../../../common/general-types/submitted-posts.dto';
import {
  CrossPlatformState,
  postStatusLabel,
  postStatusSeverity,
} from '../../../manage-brief-tabs/tabs/assignment/cross-platform-state.util';

/** Label and colour for the verification pill on a distribution row. */
export interface DistributionStatusPill {
  className: string;
  label: string;
}

/**
 * The reservations that still represent money held for this hire.
 *
 * Releasing a cross-post soft-deletes its row in place rather than writing a
 * compensating one (`WalletService.releaseCrossChannelReservation`), and `isDeleted` is
 * a plain column rather than a TypeORM delete column, so the API hands back released
 * rows alongside live ones. Summing without this filter double-counts every cancelled
 * cross-post.
 */
export function liveReservations(
  transactions: readonly WalletTransactionResponseDto[] | null | undefined,
): WalletTransactionResponseDto[] {
  return (transactions ?? []).filter(
    (transaction) =>
      transaction.isDeleted !== true &&
      transaction.reservedForPurchase === true &&
      transaction.type === WalletTransactionTypeEnum.Purchase,
  );
}

/**
 * What the brand actually committed for this hire, in major units.
 *
 * Reservations are written as negative minor units, so this is the absolute total over
 * every live row — the primary (`socialChannel: null`) plus each accepted cross-post.
 * Returns null when there are no reservations at all, which means nothing has been
 * committed yet rather than that nothing is owed.
 */
export function committedFromReservations(
  transactions: readonly WalletTransactionResponseDto[] | null | undefined,
): number | null {
  const live = liveReservations(transactions);
  if (live.length === 0) {
    return null;
  }
  const minorUnits = live.reduce(
    (total, transaction) => total + Math.abs(Number(transaction.amount) || 0),
    0,
  );
  return minorUnits / 100;
}

/**
 * The reservation held for one channel, if there is one.
 *
 * The primary channel's row carries `socialChannel: null`, so it is addressed by
 * `isPrimary` rather than by its channel number.
 */
export function reservationForChannel(
  transactions: readonly WalletTransactionResponseDto[] | null | undefined,
  socialChannel: number,
  isPrimary: boolean,
): number | null {
  const match = liveReservations(transactions).find((transaction) => {
    const channel = transaction.socialChannel;
    if (isPrimary) {
      return channel === null || channel === undefined;
    }
    // TikTok is channel 0. Number(null) is also 0, so a missing channel must not match it.
    return channel != null && Number(channel) === Number(socialChannel);
  });
  if (!match) {
    return null;
  }
  return Math.abs(Number(match.amount) || 0) / 100;
}

/**
 * Whether this channel's figure belongs in the header total.
 *
 * The primary is always part of the hire. A cross-post only is once it is assigned or
 * live — putting a number on an excluded / locked / no-account row would make the
 * visible prices fail to add up to the header.
 */
export function channelContributesToTotal(
  isPrimary: boolean,
  state: CrossPlatformState,
): boolean {
  return isPrimary || state === 'assigned' || state === 'posted';
}

/**
 * The amount shown on one distribution row, matching the Review application modal:
 * the whole primary fee on the required network, and the audience bonus only on a
 * hired cross-post.
 *
 * Null is "we cannot quote this", which is not the same as a genuine zero.
 */
export function priceForDistributionRow(input: {
  isPrimary: boolean;
  inHire: boolean;
  primaryFee: number | null;
  audienceBonus: number | null;
}): number | null {
  if (input.isPrimary) {
    return input.primaryFee;
  }
  if (!input.inHire) {
    return null;
  }
  return input.audienceBonus;
}

/**
 * Header total: every displayed row price added together.
 *
 * Returns null when there is nothing to show, so the template can render an em dash
 * rather than £0.00 before we can price anything.
 */
export function sumDisplayedPrices(
  prices: readonly (number | null | undefined)[],
): number | null {
  const amounts = prices.filter(
    (price): price is number => price != null && Number.isFinite(price),
  );
  if (amounts.length === 0) {
    return null;
  }
  return amounts.reduce((total, price) => total + price, 0);
}

/**
 * Header total as the brand is charged it: net row prices plus VAT, the same figure
 * the Review application hire button shows (`hireCostBreakdown.total`).
 *
 * Null when nothing can be quoted, so the template can render an em dash rather than
 * £0.00. VAT is applied once on the whole hire, not per row.
 */
export function displayedHireBreakdown(input: {
  primaryNet: number | null;
  crossNets: readonly (number | null | undefined)[];
  brief: BriefResponseDto | null | undefined;
  paymentMethod?: CreateBriefPaymentMethodDto | null;
}): { net: number; vat: number; total: number } | null {
  const crossAmounts = input.crossNets.filter(
    (price): price is number => price != null && Number.isFinite(price),
  );
  const hasPrimary = input.primaryNet != null && Number.isFinite(input.primaryNet);
  if (!hasPrimary && crossAmounts.length === 0) {
    return null;
  }

  const breakdown = hireCostBreakdown({
    primaryNet: hasPrimary ? Number(input.primaryNet) : 0,
    crossNet: crossPostNetTotal(crossAmounts),
    brief: input.brief,
    paymentMethod: input.paymentMethod,
  });

  return { net: breakdown.net, vat: breakdown.vat, total: breakdown.total };
}

/** The brand whose header balance should refresh after reserved funds move. */
export function walletRefreshAccountId(
  brief: { businessAccountId?: string | null } | null | undefined,
): string | null {
  const businessAccountId = brief?.businessAccountId;
  return businessAccountId ? businessAccountId : null;
}

/**
 * Header for a piece-list hire, taken from the wallet reservation.
 *
 * That total is already what the brand was charged, including VAT. Row prices on a
 * piece hire are audience fees only, so adding them up and taxing them again misses
 * the production charge and counts channels that were offered and then dropped.
 */
export function breakdownFromReservedTotal(input: {
  reserved: number;
  vatApplies: boolean;
}): { net: number; vat: number; total: number } {
  const total = input.reserved;
  if (!input.vatApplies) {
    return { net: total, vat: 0, total };
  }
  const net = Math.round((total / (1 + VAT_RATE)) * 100) / 100;
  const vat = Math.round((total - net) * 100) / 100;
  return { net, vat, total };
}

/**
 * Status pill on a distribution row.
 *
 * When a submitted post exists, this is the verification status — the same grouping
 * as the channel tile badge. Without a post it falls back to hire intent
 * (awaiting / not hired / no account).
 */
export function distributionStatusPill(
  state: CrossPlatformState,
  postStatus?: SubmittedPostStatusEnum | string | null,
): DistributionStatusPill {
  const severity = postStatusSeverity(postStatus);
  const statusLabel = postStatusLabel(postStatus);

  if (severity === 'valid') {
    return { className: 'pill pill-green shrink-0', label: 'Live' };
  }
  if (severity === 'error' && statusLabel) {
    return { className: 'pill pill-rose shrink-0', label: statusLabel };
  }
  if ((severity === 'pending' || severity === 'warning') && statusLabel) {
    return { className: 'pill pill-amber shrink-0', label: statusLabel };
  }

  switch (state) {
    case 'posted':
      return { className: 'pill pill-green shrink-0', label: 'Live' };
    case 'assigned':
      return { className: 'pill pill-amber shrink-0', label: 'Awaiting post' };
    case 'excluded':
      return { className: 'pill pill-gray shrink-0', label: 'Not hired' };
    case 'none':
      return { className: 'pill pill-gray shrink-0', label: 'No account' };
    default:
      return { className: 'pill pill-gray shrink-0', label: 'Not offered' };
  }
}

/** VAT-inclusive header figure, or null when `displayedHireBreakdown` has nothing to quote. */
export function grossDisplayedTotal(input: {
  primaryNet: number | null;
  crossNets: readonly (number | null | undefined)[];
  brief: BriefResponseDto | null | undefined;
  paymentMethod?: CreateBriefPaymentMethodDto | null;
}): number | null {
  return displayedHireBreakdown(input)?.total ?? null;
}
